import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
  PLATFORM_ID,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { GuestSessionRequest } from "../../core/models/session.model";
import { generateAnonymousName } from "../../shared/utils/anonymous-name.util";
import { UserAvatarComponent } from "../../shared/components/user-avatar/user-avatar.component";
import { AvatarPickerComponent } from "../../shared/components/avatar-picker/avatar-picker.component";
import { APP_NAME } from "../../core/constants/app.constants";

@Component({
  selector: "app-onboarding",
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    UserAvatarComponent,
    AvatarPickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./onboarding.component.html",
  styleUrl: "./onboarding.component.scss",
})
export class OnboardingComponent {
  readonly appName = APP_NAME;
  private readonly platformId = inject(PLATFORM_ID);

  readonly completed = output<GuestSessionRequest>();
  readonly displayName = signal(generateAnonymousName());
  readonly gender = signal("male");
  readonly accepted = signal(false);
  readonly avatarUrl = signal<string | null>(null);
  readonly pickerOpen = signal(false);

  randomName() {
    this.displayName.set(generateAnonymousName());
  }

  openPicker() {
    this.pickerOpen.set(true);
  }

  onPickerSelect(url: string) {
    this.avatarUrl.set(url);
    this.pickerOpen.set(false);
  }

  handleAvatarChange(event: Event) {
    if (!isPlatformBrowser(this.platformId)) return;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => this.avatarUrl.set(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  removeAvatar() {
    this.avatarUrl.set(null);
  }

  canContinue() {
    return this.displayName().trim().length >= 2 && this.accepted();
  }

  submit() {
    if (!this.canContinue()) return;
    this.completed.emit({
      displayName: this.displayName().trim(),
      gender: this.gender(),
      interests: [],
      avatarUrl: this.avatarUrl() ?? undefined,
    });
  }
}
